// Which scenario actions a viewer may attempt — DERIVED from the server's
// state machine AND the permission cell its routes require, not from a
// hand-listed button set per screen.
//
// ── WHY THIS TABLE IS RESTATED HERE, AND WHAT WATCHES IT ────────────────────
// The authority is packages/org-modeling-sdk/src/org-scenario.ts
// (`PERMITTED_TRANSITIONS` + `EDITABLE_STATES`) plus entities.ts
// (`ORG_SCENARIO_STATES` + `ORG_SCENARIO_KINDS`), which routes/org.ts consults
// through `canTransitionOrgScenario` / `evaluateScenarioEditGate`.
// @novemai/org-modeling-sdk is NOT a customer-web dependency and there is no
// alias, so the client cannot import it; adding the edge touches
// pnpm-lock.yaml, which one hand owns (FRONTEND_PORT_MAP §8 rule 5).
//
// An earlier version of this comment claimed a drift test "would be theatre"
// because Turbo would serve a cached green with no dependency edge to
// invalidate on. That reasoning was wrong about where such a gate runs: the
// `specs` job invokes twelve `*:check` scripts with plain `pnpm run`, never
// through Turbo, so they have no cache to go stale. `pnpm run scenarios:check`
// (tooling/spec-tools/check-org-scenario-client-drift.mjs) is that gate — it
// reads the SDK and this file as text and fails red when either the state set,
// the kind set, the transition table or the editable set diverges, and fails
// red rather than green when it cannot classify either side.
//
// Restating the TRANSITION TABLE (one map) rather than six per-action state
// lists is the point: five of the six predicates are answers to the same
// question the server asks — "is transition T permitted from state S" — so
// there is one thing to keep in sync, not six.
//
// ── WHERE THE TABLE IS NOT ENOUGH ───────────────────────────────────────────
// Three gates narrow FURTHER than the transition table, and reading only the
// table would over-offer:
//   * the permission cell — EVERY mutation route sits behind
//     requirePermission("org", "update", { matrix: hris/edit }); create sits
//     behind org/create → hris/create. A caller holding hris.read and not
//     hris.edit passes every state gate here and 403s on the wire.
//   * revert — `canTransition(s, "draft")` is true from shared_for_review,
//     approval_routed AND applying, but the route calls
//     OrgScenarioRepo.revertToDraft, which is hard-pinned
//     `transitionTo(..., "applying", "draft")`. Revert is applying-only.
//   * apply — the state gate is `claimForApply`'s `eq(state, 'approved')`, and
//     on top of it an in-handler `perm_org_scenario_apply_immediate` check
//     that no permission-matrix cell expresses (see api.ts's probe).
//
// Measured against services/platform-graph/src/routes/org.ts on 2026-08-07:
// create :1560, share :1876, route :1956, apply :2248 + claimForApply,
// cancel :2434, revert :2507 + :2529, edit gate :1783 block.

import type { BadgeTone } from "@novemai/components";

/** Mirrors ORG_SCENARIO_STATES (@novemai/org-modeling-sdk entities.ts). */
export type ScenarioState =
  | "draft"
  | "shared_for_review"
  | "approval_routed"
  | "approved"
  | "applying"
  | "applied"
  | "superseded"
  | "cancelled";

export const SCENARIO_STATES: readonly ScenarioState[] = [
  "draft",
  "shared_for_review",
  "approval_routed",
  "approved",
  "applying",
  "applied",
  "superseded",
  "cancelled",
];

/** Mirrors ORG_SCENARIO_KINDS (@novemai/org-modeling-sdk entities.ts). */
export const SCENARIO_KINDS: readonly string[] = [
  "reorg",
  "merge_departments",
  "split_department",
  "promotion_wave",
  "reduction_in_force",
  "cross_functional_reboot",
  "custom",
];

/**
 * The matrix cell EVERY scenario MUTATION route's preHandler requires
 * (PATCH / share / route / apply / cancel / revert all carry
 * `requirePermission("org", "update", { matrix: { module: "hris", action:
 * "edit" } })`).
 *
 * Named as the CATALOG CELL, not as "org.update": `capabilityMatches` resolves
 * the module synonym org→hris but NOT the action, so asking it for
 * "org.update" looks up a `hris.update` cell that does not exist in the
 * catalog and would deny every caller.
 */
export const SCENARIO_MUTATE_CELL = "hris.edit";

/** The matrix cell POST /v1/org/scenarios requires (org/create → hris/create). */
export const SCENARIO_CREATE_CELL = "hris.create";

const PERMITTED_TRANSITIONS: ReadonlyMap<ScenarioState, ReadonlySet<ScenarioState>> = new Map([
  ["draft", new Set<ScenarioState>(["shared_for_review", "cancelled"])],
  ["shared_for_review", new Set<ScenarioState>(["draft", "approval_routed", "cancelled"])],
  ["approval_routed", new Set<ScenarioState>(["approved", "draft", "cancelled"])],
  ["approved", new Set<ScenarioState>(["applying", "superseded", "cancelled"])],
  ["applying", new Set<ScenarioState>(["applied", "draft", "cancelled"])],
  ["applied", new Set<ScenarioState>()],
  ["superseded", new Set<ScenarioState>()],
  ["cancelled", new Set<ScenarioState>()],
]);

const EDITABLE_STATES: ReadonlySet<ScenarioState> = new Set<ScenarioState>([
  "draft",
  "shared_for_review",
]);

/**
 * Badge tone per lifecycle state. Typed as a TOTAL Record over ScenarioState so
 * a ninth state added to the union fails typecheck here instead of silently
 * rendering neutral — and it lives here, once, because both the list and the
 * detail screen draw the same badge and a second copy could only drift apart.
 */
const STATE_TONE: Readonly<Record<ScenarioState, BadgeTone>> = {
  draft: "neutral",
  shared_for_review: "info",
  approval_routed: "info",
  approved: "success",
  applying: "warning",
  applied: "success",
  superseded: "neutral",
  cancelled: "danger",
};

/**
 * The role the revert route accepts INSTEAD of authorship
 * (routes/org.ts:2529). One id, matching the server's own literal — not a
 * copied role SET. If it ever drifts, an architect who is not the author loses
 * the button while the author path (the escape hatch spec 68 documents) keeps
 * working: fail-closed.
 */
export const ORG_ARCHITECT_ROLE = "role_org_architect";

/**
 * `true` when the string is one of the eight known states. An unrecognized
 * state offers NO actions rather than defaulting to draft's — a server that
 * grew a ninth state must not have its buttons guessed at.
 */
export function isScenarioState(value: string): value is ScenarioState {
  return PERMITTED_TRANSITIONS.has(value as ScenarioState);
}

/** Tone for a wire state string; an unknown state reads neutral, never guessed. */
export function scenarioStateTone(state: string): BadgeTone {
  return isScenarioState(state) ? STATE_TONE[state] : "neutral";
}

/**
 * The server's edit gate (`evaluateScenarioEditGate`), exported so the screen
 * can close an open edit form on a state change without restating the pair of
 * editable state literals a fourth time.
 */
export function isEditableScenarioState(state: string): boolean {
  return isScenarioState(state) && EDITABLE_STATES.has(state);
}

function canTransition(from: string, to: ScenarioState): boolean {
  if (!isScenarioState(from)) return false;
  return PERMITTED_TRANSITIONS.get(from)?.has(to) ?? false;
}

export interface ScenarioActionInput {
  readonly state: string;
  /** `proposed_by` — a JWT subject, compared against the viewer's identity_id. */
  readonly proposedBy: string;
  /** /v1/me `identity_id`; null while the identity query is in flight. */
  readonly viewerIdentityId: string | null;
  /** /v1/me `roles` — the server's own resolved roles, never a client guess. */
  readonly viewerRoles: readonly string[];
  /**
   * Whether the caller's resolved permission set holds SCENARIO_MUTATE_CELL.
   * False while the effective-permissions query is in flight (default-closed).
   */
  readonly canMutate: boolean;
  /** The /v1/permissions/check answer for perm_org_scenario_apply_immediate. */
  readonly canApplyImmediately: boolean;
}

export interface ScenarioActionAvailability {
  readonly edit: boolean;
  readonly share: boolean;
  readonly route: boolean;
  /** Whether to RENDER apply — the state gate plus the coarse permission cell. */
  readonly apply: boolean;
  /** Whether apply is CLICKABLE — the above AND the granular permission. */
  readonly applyEnabled: boolean;
  readonly cancel: boolean;
  readonly revert: boolean;
}

export function scenarioActions(input: ScenarioActionInput): ScenarioActionAvailability {
  const { state, canMutate } = input;

  // The COARSE cell hides, like every other ScopeGate in the product; only the
  // GRANULAR apply permission renders disabled-with-a-reason. A caller who
  // holds neither would otherwise see one lone disabled button explaining a
  // permission they were never close to needing.
  const applyStateOk = canMutate && canTransition(state, "applying");

  const revertStateOk = canMutate && state === "applying";
  const viewerIsAuthor = input.viewerIdentityId === input.proposedBy;

  return {
    edit: canMutate && isEditableScenarioState(state),
    share: canMutate && canTransition(state, "shared_for_review"),
    route: canMutate && canTransition(state, "approval_routed"),
    apply: applyStateOk,
    applyEnabled: applyStateOk && input.canApplyImmediately,
    cancel: canMutate && canTransition(state, "cancelled"),
    revert:
      revertStateOk && (viewerIsAuthor || input.viewerRoles.includes(ORG_ARCHITECT_ROLE)),
  };
}

/**
 * A scenario stuck mid-apply: `applying` with a recorded halt_reason. The
 * apply saga records the halt in a separate transaction after rolling the
 * mutation back, so this is "nothing was applied and someone must act", not
 * "partially applied".
 */
export function isHalted(state: string, haltReason: unknown): boolean {
  return state === "applying" && haltReason !== null && haltReason !== undefined;
}
